import React, { useMemo } from 'react';
import { Event } from '../../../types/event.types';
import { FormData } from '../EventForm/types';
import { formatTimeString } from '../../../utils/dateFormatter';
import { formatEventDateTime, formatEventDateForIcon } from '../../../utils/timezoneUtils';
import {
  Icon,
  Tag,
  DateIcon,
  GlassPanel,
  LogoSVG
} from '../../../pages/DesignSystem';
import { useOrganizer } from '../../../contexts/OrganizerContext';
import '../../../components/event/EventModal.css';
import '../../../components/event/card.css';

interface EventLivePreviewProps {
  formData: Partial<FormData>;
  currentStep: number;
}

/**
 * EventLivePreview - Real-time preview using EventModal visual style
 * Converts FormData to Event format for accurate preview
 */
const EventLivePreview: React.FC<EventLivePreviewProps> = ({
  formData,
  currentStep
}) => {
  // Get organizer profile from context
  const { organizerProfile } = useOrganizer();

  // Convert FormData to Event format for preview
  const previewEvent: Partial<Event> = useMemo(() => {
    // Build datetime from date and time
    let datetime: Date | undefined;
    if (formData.isRecurring && formData.startDate && formData.startTime) {
      datetime = new Date(`${formData.startDate}T${formData.startTime}`);
    } else if (formData.date && formData.time) {
      datetime = new Date(`${formData.date}T${formData.time}`);
    }

    return {
      id: 'preview',
      organizerId: formData.organizerId || '',
      timezone: formData.timezone || 'Asia/Colombo',
      title: formData.title || 'Your Event Title',
      description: formData.description,
      datetime,
      endTime: formData.endTime,
      time: formData.time || formData.startTime,
      location: formData.location,
      locationName: formData.locationName,
      latitude: formData.latitude,
      longitude: formData.longitude,
      tags: formData.tags || [],
      image: formData.image,
      prices: formData.prices,
      ticketingUrl: formData.ticketingUrl,
      status: formData.status || 'draft',
      seriesId: formData.isRecurring ? 'preview-series' : undefined,
      weekDays: formData.weekDays,
      seriesFrequency: formData.frequency
    };
  }, [formData]);

  // Format time display
  const eventTime = useMemo(() => {
    const time = formData.time || formData.startTime;
    if (!time) return '';
    return `${formatTimeString(time)}${formData.endTime ? ` - ${formatTimeString(formData.endTime)}` : ''}`;
  }, [formData.time, formData.startTime, formData.endTime]);

  // Days order for sorting
  const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Build recurring pattern text - matching useSmartSeries.ts format
  const recurringPattern = useMemo(() => {
    if (!formData.isRecurring) return null;

    const capitalizeFirstLetter = (str: string) => {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    let pattern = '';

    if (formData.frequency === 'daily') {
      pattern = 'Every day';
    } else {
      // Weekly mode
      const days = formData.weekDays || [];

      if (days.length === 0) {
        pattern = 'Choose your days';
      } else if (days.length === 7) {
        pattern = 'Every day';
      } else if (days.length >= 5) {
        // Show "Every day except [missing days]"
        const selectedDaysLower = days.map(d => d.toLowerCase());
        const missingDays = ALL_DAYS.filter(day => !selectedDaysLower.includes(day.toLowerCase()));
        if (missingDays.length === 1) {
          pattern = `Every day except ${missingDays[0]}`;
        } else {
          const lastDay = missingDays.pop();
          pattern = `Every day except ${missingDays.join(', ')} and ${lastDay}`;
        }
      } else {
        // Sort days and format as "Every Monday, Tuesday and Wednesday"
        const sortedDays = days
          .map(d => d.toLowerCase())
          .sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b))
          .map(capitalizeFirstLetter);

        if (sortedDays.length === 1) {
          pattern = `Every ${sortedDays[0]}`;
        } else {
          const lastDay = sortedDays.pop();
          pattern = `Every ${sortedDays.join(', ')} and ${lastDay}`;
        }
      }
    }

    // Add end date to pattern (like useSmartSeries)
    let endDateStr = '';
    if (formData.startDate) {
      let endDate: Date | null = null;

      if (formData.endType === 'date' && formData.endDate) {
        endDate = new Date(formData.endDate);
      } else if (formData.endType === 'occurrences' && formData.occurrences) {
        endDate = new Date(formData.startDate);
        endDate.setDate(endDate.getDate() + (formData.occurrences * 7));
      }

      if (endDate) {
        endDateStr = ` until ${endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
      }
    }

    return pattern + endDateStr;
  }, [formData.isRecurring, formData.frequency, formData.weekDays, formData.startDate, formData.endType, formData.endDate, formData.occurrences]);

  const hasImage = Boolean(formData.image);
  const hasBasicInfo = formData.title || formData.date || formData.location;

  // Render tags
  const renderTags = () => {
    if (!previewEvent.tags || previewEvent.tags.length === 0) return null;
    return (
      <div className="event-card__tags">
        {previewEvent.tags.map(tag => (
          <Tag
            key={tag}
            label={tag}
            type="activity"
            size="md"
          />
        ))}
      </div>
    );
  };

  // Render prices - single icon with stacked prices, sorted cheapest first
  const renderPrices = () => {
    // No prices at all → show nothing
    if (!previewEvent.prices || previewEvent.prices.length === 0) {
      return null;
    }

    // Check if all prices have amount === 0 → Free
    const allFree = previewEvent.prices.every(price => price.amount === 0);
    if (allFree) {
      return (
        <div className="event-card__glass-row">
          <div className="event-card__glass-icon">
            <Icon name="Ticket" size={20} />
          </div>
          <span className="event-card__glass-price event-card__glass-price--free">Free</span>
        </div>
      );
    }

    // Sort prices: null amounts last, then by amount ascending
    const sortedPrices = [...previewEvent.prices].sort((a, b) => {
      if (a.amount === null && b.amount === null) return 0;
      if (a.amount === null) return 1;
      if (b.amount === null) return -1;
      return a.amount - b.amount;
    });

    return (
      <div className="event-card__glass-row">
        <div className="event-card__glass-icon">
          <Icon name="Ticket" size={20} />
        </div>
        <div className="event-card__glass-info">
          {sortedPrices.map((price, index) => (
            <span key={index} className={`event-card__glass-price${price.amount === null ? ' event-card__glass-price--info' : ''}`}>
              {price.amount === null
                ? (price.description || 'Contact organizer')
                : price.amount === 0
                  ? 'Free'
                  : `${price.amount}${price.currency === 'EUR' ? '€' : price.currency}`}
              {price.amount !== null && price.description && ` - ${price.description}`}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="event-live-preview">
      {/* Preview container styled like EventModal */}
      <div className="event-live-preview__modal-wrapper">
        <div className={`event-modal ${hasImage ? 'event-modal--with-image' : 'event-modal--fallback'}`}>
          {/* Background Image */}
          <div className={`event-modal__background ${!hasImage ? 'event-modal__background--fallback' : ''}`}>
            {hasImage ? (
              <img
                src={formData.image}
                alt={previewEvent.title}
                className="loaded"
              />
            ) : (
              <LogoSVG
                variant="vertTexte"
                size="auto"
                padding={0}
              />
            )}
          </div>

          {/* Card Section - Like EventModal */}
          <div className={`event-modal__card-section ${hasImage ? 'event-modal__card-section--with-image' : 'event-modal__card-section--fallback'}`}>
            {/* Header with tags */}
            <div className="event-card__header">
              {renderTags()}
            </div>

            {/* Spacer */}
            <div className="event-card__spacer" />

            {/* Main GlassPanel */}
            <div className="event-card__glass-container">
              <GlassPanel variant="light" blur="md" radius="md">
                <div className="event-card__glass-content">
                  {/* Title */}
                  <h3 className="event-card__glass-title">
                    {previewEvent.title || 'Your Event Title'}
                  </h3>

                  {/* Date/Pattern + Time */}
                  {formData.isRecurring ? (
                    <div className="event-card__glass-row">
                      <div className="event-card__glass-icon">
                        <Icon name="Repeat" size={20} />
                      </div>
                      <div className="event-card__glass-info">
                        <span className="event-card__glass-primary">
                          {recurringPattern}
                        </span>
                        {eventTime && (
                          <span className="event-card__glass-secondary">
                            {eventTime}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : previewEvent.datetime ? (
                    <div className="event-card__glass-row">
                      <div className="event-card__glass-icon">
                        <DateIcon
                          {...formatEventDateForIcon(previewEvent.datetime, previewEvent.timezone || 'Asia/Colombo')}
                          variant="dark"
                          size="sm"
                        />
                      </div>
                      <div className="event-card__glass-info">
                        <span className="event-card__glass-primary">
                          {formatEventDateTime(previewEvent.datetime, previewEvent.timezone || 'Asia/Colombo').weekday}
                        </span>
                        {eventTime && (
                          <span className="event-card__glass-secondary">
                            {eventTime}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="event-card__glass-row event-card__glass-row--placeholder">
                      <div className="event-card__glass-icon">
                        <Icon name="Calendar" size={20} />
                      </div>
                      <span className="event-card__glass-placeholder">Date not set</span>
                    </div>
                  )}

                  {/* Location */}
                  <div className="event-card__glass-row">
                    <div className="event-card__glass-icon">
                      <Icon name="MapPin" size={20} />
                    </div>
                    {previewEvent.locationName || previewEvent.location ? (
                      <div className="event-card__glass-location-text">
                        <span className="event-card__glass-location-name">
                          {previewEvent.locationName || previewEvent.location}
                        </span>
                      </div>
                    ) : (
                      <span className="event-card__glass-placeholder">Location not set</span>
                    )}
                  </div>

                  {/* Prices - one per line */}
                  {renderPrices()}
                </div>
              </GlassPanel>
            </div>
          </div>

          {/* Details Section */}
          <div className="event-modal__details">
            {/* Booking URL - show exact URL entered by organizer */}
            {previewEvent.ticketingUrl && (
              <GlassPanel variant="light" blur="md" radius="md">
                <div className="event-card__glass-content">
                  <div className="event-card__glass-row">
                    <div className="event-card__glass-icon">
                      <Icon name="ExternalLink" size={20} />
                    </div>
                    <div className="event-card__glass-info">
                      <span className="event-card__glass-primary event-card__glass-primary--link">
                        {previewEvent.ticketingUrl}
                      </span>
                    </div>
                  </div>
                </div>
              </GlassPanel>
            )}

            {/* Organizer Profile + Description in one block */}
            <GlassPanel variant="light" blur="md" radius="md">
              <div className="event-card__glass-content">
                {/* Organizer Profile Row */}
                <div className="event-card__organizer-row">
                  <div className="event-card__organizer-avatar">
                    {organizerProfile?.organizerImage ? (
                      <img
                        src={organizerProfile.organizerImage}
                        alt={organizerProfile.organizer || 'Organizer'}
                        className="event-card__organizer-avatar-img"
                      />
                    ) : (
                      <span className="event-card__organizer-avatar-letter">
                        {(organizerProfile?.organizer || 'O').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="event-card__organizer-info">
                    <span className="event-card__organizer-name">
                      {organizerProfile?.organizer || 'Your Organizer Profile'}
                    </span>
                    {organizerProfile?.subtitle && (
                      <span className="event-card__organizer-subtitle">
                        {organizerProfile.subtitle}
                      </span>
                    )}
                  </div>
                  <Icon name="ChevronRight" size={14} className="event-card__organizer-chevron" />
                </div>

                {/* Description with border separator */}
                <p className={`event-card__glass-description event-card__glass-description--with-separator ${!previewEvent.description ? 'event-card__glass-description--placeholder' : ''}`}>
                  {previewEvent.description || 'Your event description will appear here...'}
                </p>
              </div>
            </GlassPanel>
          </div>
        </div>
      </div>

      {/* Helper text */}
      <div className="event-live-preview__helper">
        {currentStep === 0 && !hasBasicInfo && (
          <p>Start by entering your event details on the left</p>
        )}
        {currentStep === 0 && hasBasicInfo && (
          <p>Looking good! Continue to add more details</p>
        )}
        {currentStep === 1 && (
          <p>Add a description and image to make your event stand out</p>
        )}
        {currentStep === 2 && (
          <p>Review your event before publishing</p>
        )}
      </div>
    </div>
  );
};

export default EventLivePreview;
